import React, { useEffect, useState } from 'react';
import { Card, CardBody, Collapse, Button } from 'reactstrap';
import { Form } from '@availity/form';
import { Select, Input } from '../../lib/form-field';
import * as messageTemplatesActions from '../../store/messageTemplates/actions';
import * as callsActions from '../../store/calls/actions';
import { selectMessageTemplate } from '../../selectors/messageTemplate';
import { useDispatch, useSelector } from 'react-redux';

import { useTranslation } from 'react-i18next';
import { replaceMacro } from '../../helpers/sharedFunction';
import Alert from '../Common/Modal/Alert';

const SendMessageOnCall = ({ acquisitionOrder, lead }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMessageTemplateId, setSelectedMessageTemplateId] =
    useState('');
  const [showConfirmAlert, setShowConfirmAlert] = useState(false);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const callMessageTemplateIds = acquisitionOrder?.callMessageTemplateIds;
  const messageTemplates = useSelector(
    (state) => state.MessageTemplate.messageTemplates,
  );
  const selectedMessageTemplate = useSelector(
    selectMessageTemplate(selectedMessageTemplateId),
  );

  // Filter message templates based on callMessageTemplateIds
  const availableTemplates = messageTemplates.filter((template) =>
    callMessageTemplateIds?.includes(template.id),
  );

  const loadingCalls = useSelector((state) => state.Call.loading);
  const loadingMessages = useSelector((state) => state.MessageTemplate.loading);

  const loading = loadingCalls || loadingMessages;

  useEffect(() => {
    dispatch(messageTemplatesActions.syncMessageTemplates());
  }, [dispatch]);

  const handleTemplateChange = (value) => {
    setSelectedMessageTemplateId(value);
  };

  const handleSendMessage = async () => {
    if (!selectedMessageTemplateId || !lead?.phone) {
      return;
    }

    setShowConfirmAlert(true);
  };

  const handleConfirmSendMessage = async () => {
    dispatch(
      callsActions.createMessageOnCall(selectedMessageTemplateId, lead.id),
    );

    setSelectedMessageTemplateId('');
    setIsOpen(false);
    setShowConfirmAlert(false);
  };

  if (
    !Array.isArray(callMessageTemplateIds) ||
    callMessageTemplateIds.length === 0 ||
    availableTemplates.length === 0
  ) {
    return null;
  }

  return (
    <React.Fragment>
      <Card>
        <CardBody>
          <span
            onClick={() => {
              setIsOpen(!isOpen);
            }}
            className="d-flex align-items-center"
            style={{ cursor: 'pointer' }}
          >
            <h4 className="card-title mb-4">{t('Send Message')}</h4>
            {loading ? (
              <i className="bx bx-loader-circle bx-spin mb-4 font-size-17 ml-1"></i>
            ) : (
              <i
                className={`mdi mdi-chevron-${
                  isOpen ? 'up' : 'down'
                } mb-4 font-size-17 ml-1`}
              ></i>
            )}
            {loading && (
              <p className="mb-4 font-size-13 ml-2">{t('Sending...')}</p>
            )}
          </span>
          <Collapse isOpen={isOpen}>
            <Form
              initialValues={{ messageTemplateId: selectedMessageTemplateId }}
              onSubmit={() => {}}
            >
              {
                <>
                  <Select
                    name="messageTemplateId"
                    id="messageTemplateId"
                    label={t('Select Template')}
                    options={availableTemplates.map(({ id, name }) => ({
                      value: id,
                      label: name,
                    }))}
                    onChange={handleTemplateChange}
                    isMulti={false}
                  />

                  {selectedMessageTemplateId && (
                    <>
                      <Input
                        id="subject"
                        name="subject"
                        label={t('Sender')}
                        readOnly
                        value={selectedMessageTemplate.subject}
                      />
                      <Input
                        id="messageText"
                        name="messageText"
                        label={t('Message')}
                        type="textarea"
                        rows="10"
                        readOnly
                        value={replaceMacro(
                          selectedMessageTemplate.messageText || '',
                          lead || {},
                          false,
                        )}
                      />
                    </>
                  )}

                  {selectedMessageTemplateId && (
                    <>
                      <div className="d-flex justify-content-between">
                        <Button
                          color="success"
                          size="sm"
                          className="waves-effect waves-light"
                          onClick={handleSendMessage}
                          disabled={loading || !lead?.phone}
                        >
                          <i
                            className={`bx bx-send font-size-20 align-middle mr-2 ${
                              loading ? 'bx-spin' : ''
                            }`}
                          ></i>
                          {loading ? t('Sending...') : t('Send Message')}
                        </Button>
                      </div>
                    </>
                  )}
                </>
              }
            </Form>
          </Collapse>
        </CardBody>
      </Card>

      <Alert
        isOpen={showConfirmAlert}
        title={t('Confirm Message Send')}
        confirmBtnLabel={t('Send')}
        onCancel={() => setShowConfirmAlert(false)}
        onConfirm={handleConfirmSendMessage}
      >
        {selectedMessageTemplate && (
          <div>
            <p>{t('Are you sure you want to send this message?')}</p>
            <div className="mt-3 p-3 bg-light rounded">
              <div className="mb-2">
                <strong>{t('Sender')}:</strong>
                <span className="ml-2">{selectedMessageTemplate.subject}</span>
              </div>
              <div>
                <div
                  className="mt-3 text-left"
                  style={{ whiteSpace: 'pre-line' }}
                >
                  {replaceMacro(
                    selectedMessageTemplate.messageText || '',
                    lead || {},
                    false,
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Alert>
    </React.Fragment>
  );
};

export default SendMessageOnCall;
